import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectorRef,
    ComponentRef,
    ViewContainerRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { LocationMapComponent, MapLocation } from '../location-map/location-map.component';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EMPTY, Subject, catchError, defaultIfEmpty, finalize, of, switchMap, take, takeUntil } from 'rxjs';
import { ProjectService } from '../../../core/services/project.service';
import { ProjectDetailsModel } from '../../../core/models/project-info/ProjectDetails.model';
import { ProjectUpdateRequest } from '../../../core/models/project-info/ProjectUpdateRequest.model';
import { ProjectLocationModel } from '../../../core/models/project/ProjectLocationModel.model';

type ProjectTab = 'general' | 'access' | 'usage' | 'technical' | 'location' | 'notifications';
export interface ProjectNotificationModel { Email: string; Phone: string; }
export interface ProjectSettingsEndpoints { location: string; lookup: string; notifications: string; }
export interface ProjectSettingsAPIResult<T> { data: T; isError: boolean; msg: string; }
type SettingsOperation = 'load-location' | 'lookup' | 'save-location' | 'load-notifications' | 'email' | 'phone';
type UpdateOperation = 'save-location' | 'email' | 'phone';
type NotificationField = 'Email' | 'Phone';

type Feedback = { type: 'success' | 'error'; message: string } | null;

@Component({
    selector: 'app-project-details',
    standalone: false,
    templateUrl: './project-info.html',
    styleUrls: ['./project-info.css'],
})
export class ProjectInfo implements OnInit, OnChanges, OnDestroy {
    @Input() project: ProjectDetailsModel | null = null;

    @Output() saved = new EventEmitter<ProjectDetailsModel>();
    @Output() deleted = new EventEmitter<string>();
    @Output() back = new EventEmitter<void>();

    @ViewChild('deleteDialog', { static: true })
    private deleteDialog!: ElementRef<HTMLDialogElement>;
    @ViewChild('deleteInput', { static: true }) private deleteInput!: ElementRef<HTMLInputElement>;
    @ViewChild('feedbackPanel') private feedbackPanel?: ElementRef<HTMLElement>;

    readonly tabs: { id: ProjectTab; label: string; icon: string }[] = [
        { id: 'general', label: 'Genel Bilgiler', icon: 'bi bi-sliders' },
        { id: 'access', label: 'Erişim Ayarları', icon: 'bi bi-shield-lock' },
        { id: 'usage', label: 'Kullanım ve İstatistikler', icon: 'bi bi-bar-chart' },
        { id: 'technical', label: 'Teknik Bilgiler', icon: 'bi bi-code-square' },
        { id: 'location', label: 'Lokasyon', icon: 'bi bi-geo-alt' },
        { id: 'notifications', label: 'Bildirimler', icon: 'bi bi-bell' },
    ];

    readonly icons = [
        'bi bi-box',
        'bi bi-briefcase',
        'bi bi-code-slash',
        'bi bi-cloud',
        'bi bi-cpu',
        'bi bi-database',
        'bi bi-globe',
        'bi bi-layers',
        'bi bi-lightning',
        'bi bi-rocket-takeoff',
        'bi bi-three-dots',
    ];

    readonly form = new FormGroup({
        name: new FormControl('', {
            nonNullable: true,
            validators: [Validators.required, Validators.maxLength(180), Validators.pattern(/\S/)],
        }),
        description: new FormControl('', { nonNullable: true }),
        icon: new FormControl('bi bi-box', { nonNullable: true }),
        color: new FormControl('#1f6feb', {
            nonNullable: true,
            validators: [Validators.pattern(/^(?:#[0-9a-fA-F]{6})?$/)],
        }),
        isActive: new FormControl(true, { nonNullable: true }),
        isFavorite: new FormControl(false, { nonNullable: true }),
        lang: new FormControl('tr', { nonNullable: true, validators: [Validators.required] }),
        coverImageUrl: new FormControl('', { nonNullable: true }),
        allowedDomains: new FormControl<string[]>([], { nonNullable: true }),
    });

    readonly domainControl = new FormControl('', { nonNullable: true });
    readonly deleteNameControl = new FormControl('', { nonNullable: true });

    private readonly destroyed$ = new Subject<void>();
    private original: ProjectDetailsModel | null = null;
    private initialValues = this.form.getRawValue();
    private deleteTrigger: HTMLElement | null = null;

    currentTab: ProjectTab = 'general';
    feedback: Feedback = null;
    domainError = '';
    deleteError = '';
    busy: 'save' | 'delete' | null = null;
    isDeleted = false;
    showAddress = false;
    showToken = false;
    logoFailed = false;
    projectId: string | null = null;

    // Lokasyon ve bildirim kayıtları mevcut endpoint'leri kullanır; okuma ProjectService.GetProject üzerinden yapılır.
    @Input() settingsApi: ProjectSettingsEndpoints = { location: '', lookup: '', notifications: '' };
    location: ProjectLocationModel = new ProjectLocationModel();
    readonly ipControl = new FormControl('', { nonNullable: true });
    readonly notificationEmailControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email, Validators.maxLength(180)] });
    readonly notificationPhoneControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^\+?[0-9 ()-]{7,25}$/)] });
    ipEditing = false;
    emailEditing = false;
    phoneEditing = false;
    locationDirty = false;
    locationAvailable = false;
    locationFeedback: Feedback = null;
    notificationFeedback: Feedback = null;
    settingsBusy: SettingsOperation | null = null;
    savedNotifications: ProjectNotificationModel = { Email: '', Phone: '' };
    private settingsProjectId = '';
    private locationLoaded = false;
    private notificationsLoaded = false;
    private mapRef?: ComponentRef<LocationMapComponent>;

    @ViewChild('locationMapHost', { read: ViewContainerRef })
    set locationMapHost(host: ViewContainerRef | undefined) {
        this.mapRef?.destroy();
        this.mapRef = undefined;
        if (!host) return;
        const ref = host.createComponent(LocationMapComponent);
        this.mapRef = ref;
        ref.setInput('height', 420);
        this.syncLocationMap();
        const subscription = ref.instance.locationSelected.subscribe(value => this.onLocationSelected(value));
        ref.onDestroy(() => subscription.unsubscribe());
    }

    get settingsLocked(): boolean { return this.busy !== null || this.settingsBusy !== null || this.isDeleted; }
    get locationRows(): { label: string; value: string }[] { return [{ label: 'Ülke', value: this.location.CountryName }, { label: 'Şehir', value: this.location.CityName }, { label: 'Ülke kodu', value: this.location.CountryCode }, { label: 'Bölge', value: this.location.Region }, { label: 'Saat dilimi', value: this.location.TimeZone }, { label: 'Enlem / Latitude', value: this.locationAvailable ? this.location.Latitude.toFixed(6) : '—' }, { label: 'Boylam / Longitude', value: this.locationAvailable ? this.location.Longitude.toFixed(6) : '—' }]; }
    toggleIpEditing(): void { if (this.settingsLocked) return; this.ipEditing = !this.ipEditing; if (!this.ipEditing) this.ipControl.setValue(this.location.Ip); }
    toggleNotificationEditing(field: NotificationField): void { if (this.settingsLocked) return; if (field === 'Email') { this.emailEditing = !this.emailEditing; if (!this.emailEditing) this.notificationEmailControl.reset(this.savedNotifications.Email); } else { this.phoneEditing = !this.phoneEditing; if (!this.phoneEditing) this.notificationPhoneControl.reset(this.savedNotifications.Phone); } }

    onLocationSelected(value: MapLocation): void {
        if (this.settingsLocked) { this.syncLocationMap(); return; }
        if (this.locationAvailable && value.Latitude === this.location.Latitude && value.Longitude === this.location.Longitude) return;
        this.location = ProjectLocationModel.fromCoordinates(value.Latitude, value.Longitude).setIp(this.location.Ip || this.ipControl.value.trim());
        this.locationAvailable = true;
        this.locationDirty = true;
        this.locationFeedback = { type: 'success', message: 'Yeni konum seçildi. Kaydet butonuyla projeye uygulayabilirsiniz.' };
        this.syncLocationMap();
        this.cdr.markForCheck();
    }

    checkLocationIp(): void {
        if (this.settingsLocked || !this.original) return;
        const ip = this.ipControl.value.trim();
        if (!this.validIpAddress(ip)) {
            this.locationFeedback = { type: 'error', message: 'Geçerli bir IPv4 veya IPv6 adresi giriniz.' };
            return;
        }

        this.settingsBusy = 'lookup';
        this.locationFeedback = null;
        this.syncLocationMap();
        this.projectSvc.GetLocationFromIp(ip)
            .pipe(take(1), defaultIfEmpty(null), takeUntil(this.destroyed$), finalize(() => { this.settingsBusy = null; this.syncLocationMap(); this.cdr.markForCheck(); }))
            .subscribe({
                next: result => {
                    if (!result || result.isError || !result.data) {
                        this.locationFeedback = { type: 'error', message: result?.msg || 'IP adresine ait lokasyon bulunamadı.' };
                        return;
                    }
                    const location = ProjectLocationModel.fromApi(result.data);
                    if (!location) {
                        this.locationFeedback = { type: 'error', message: 'API geçerli bir enlem ve boylam döndürmedi.' };
                        return;
                    }
                    this.location = location.setIp(ip);
                    this.ipControl.setValue(ip);
                    this.ipEditing = false;
                    this.locationAvailable = true;
                    this.locationDirty = true;
                    this.locationLoaded = true;
                    this.locationFeedback = { type: 'success', message: 'IP konumu haritada gösteriliyor. Kaydet butonuyla projeye uygulayabilirsiniz.' };
                    this.syncLocationMap();
                    this.cdr.markForCheck();
                },
                error: error => {
                    this.locationFeedback = { type: 'error', message: this.errorText(error, 'IP sorgulanırken bağlantı hatası oluştu.') };
                    this.cdr.markForCheck();
                }
            });
    }

    // Eski çağrılar kırılmasın diye ikinci metot sadece asıl sorguya yönlendirilir.
    checkLocationIp2(): void { this.checkLocationIp(); }

    saveLocation(): void {
        if (this.settingsLocked || !this.original || !this.locationAvailable || !this.locationDirty) return;
        const url = this.settingsUrl('location');
        if (!url) return;
        const location = this.location.clone().toJSON();
        this.locationFeedback = null;
        this.updateSetting('save-location', url, { projectId: this.original.id, location }, () => {
            this.locationDirty = false;
            this.locationLoaded = true;
            this.ipEditing = false;
            this.ipControl.setValue(location.Ip);
            Object.assign(this.original!, { location });
            if (this.project) Object.assign(this.project, { location: { ...location } });
            this.locationFeedback = { type: 'success', message: 'Lokasyon bilgileri başarıyla saklandı.' };
            this.saved.emit(this.clone(this.original!));
        });
    }

    saveNotification(field: NotificationField): void {
        if (this.settingsLocked || !this.original || (field === 'Email' ? !this.emailEditing : !this.phoneEditing)) return;
        const control = field === 'Email' ? this.notificationEmailControl : this.notificationPhoneControl;
        control.setValue(control.value.trim());
        control.markAsTouched();
        if (control.invalid || (field === 'Phone' && control.value.replace(/\D/g, '').length < 7)) {
            this.notificationFeedback = { type: 'error', message: field === 'Email' ? 'Geçerli bir bildirim e-posta adresi giriniz.' : 'Geçerli bir bildirim telefon numarası giriniz.' };
            return;
        }
        const url = this.settingsUrl('notifications');
        if (!url) return;
        const notifications = { ...this.savedNotifications, [field]: control.value };
        this.notificationFeedback = null;
        this.updateSetting(field === 'Email' ? 'email' : 'phone', url, { projectId: this.original.id, field, value: control.value }, () => {
            this.savedNotifications = notifications;
            if (field === 'Email') this.emailEditing = false; else this.phoneEditing = false;
            control.markAsPristine();
            Object.assign(this.original!, { notifications: { ...notifications } });
            if (this.project) Object.assign(this.project, { notifications: { ...notifications } });
            this.notificationFeedback = { type: 'success', message: field === 'Email' ? 'Bildirim e-posta adresi güncellendi.' : 'Bildirim telefon numarası güncellendi.' };
            this.saved.emit(this.clone(this.original!));
        });
    }

    private settingsUrl(key: 'location' | 'notifications'): string | null {
        const template = this.settingsApi?.[key]?.trim();
        if (!template || !this.original) {
            const feedback: Feedback = { type: 'error', message: 'Bu işlem için bağlantı henüz yapılandırılmamış. Sistem yöneticinizle iletişime geçiniz.' };
            if (key === 'notifications') this.notificationFeedback = feedback; else this.locationFeedback = feedback;
            return null;
        }
        return template.replace(/\{id\}/g, encodeURIComponent(this.original.id));
    }

    // Lokasyon / bildirimlerin var olan API sözleşmesi korunur; ortak kayıt akışı kullanılır.
    private updateSetting(operation: UpdateOperation, url: string, body: unknown, onSuccess: () => void): void {
        this.settingsBusy = operation;
        this.syncLocationMap();
        this.http.put<ProjectSettingsAPIResult<boolean>>(url, body)
            .pipe(take(1), defaultIfEmpty(null), takeUntil(this.destroyed$), finalize(() => {
                this.settingsBusy = null;
                this.syncLocationMap();
                this.cdr.markForCheck();
            }))
            .subscribe({
                next: result => {
                    if (!result || result.isError || result.data !== true) {
                        this.updateFeedback(operation, result?.msg || 'Değişiklikler kaydedilemedi.');
                        return;
                    }
                    onSuccess();
                    this.cdr.markForCheck();
                },
                error: error => this.updateFeedback(operation, this.errorText(error, 'Güncelleme sırasında bağlantı hatası oluştu.'))
            });
    }

    // Yenile butonları ortak GetProject sorgusunu kullanır; bağımsız ayar API'leri kullanılmaz.
    loadLocation(): void {
        if (this.settingsLocked || !this.original || this.locationDirty) return;
        this.getProject('location');
    }

    loadNotifications(): void {
        if (this.settingsLocked || !this.original || this.emailEditing || this.phoneEditing) return;
        this.getProject('notifications');
    }

    private getProject(section?: 'location' | 'notifications'): void {
        const id = this.original?.id || this.projectId;
        if (!id) { this.notify('error', 'Route içerisinde proje ID bulunamadı.'); return; }
        if (section) this.settingsBusy = section === 'location' ? 'load-location' : 'load-notifications';
        this.projectSvc.GetProject(id)
            .pipe(take(1), defaultIfEmpty(null), takeUntil(this.destroyed$), finalize(() => {
                if (section) this.settingsBusy = null;
                this.syncLocationMap();
                this.cdr.markForCheck();
            }))
            .subscribe({
                next: result => {
                    if (id !== (this.original?.id || this.projectId)) return;
                    if (!result || result.isError || !result.data) {
                        const message = result?.msg || 'Proje bilgileri alınamadı.';
                        if (section === 'location') this.locationFeedback = { type: 'error', message };
                        else if (section === 'notifications') this.notificationFeedback = { type: 'error', message };
                        else this.notify('error', message);
                        return;
                    }
                    if (!section) { this.applyProject(result.data); return; }
                    if (section === 'location') {
                        const value = this.readSettingsField(result.data, ['location']);
                        const location = ProjectLocationModel.fromApi(value);
                        this.location = location || new ProjectLocationModel();
                        this.locationAvailable = !!location;
                        this.locationLoaded = true;
                        this.locationDirty = false;
                        this.ipControl.reset(this.location.Ip);
                        this.ipEditing = false;
                        this.locationFeedback = null;
                        Object.assign(this.original!, { location: value });
                        if (this.project) Object.assign(this.project, { location: value });
                    } else {
                        const value = this.readSettingsField(result.data, ['notifications']);
                        this.applyNotifications(value);
                        this.notificationsLoaded = true;
                        this.notificationFeedback = null;
                        Object.assign(this.original!, { notifications: value });
                        if (this.project) Object.assign(this.project, { notifications: value });
                    }
                    this.syncLocationMap();
                },
                error: error => {
                    const message = this.errorText(error, 'Proje yüklenirken bağlantı hatası oluştu.');
                    if (section === 'location') this.locationFeedback = { type: 'error', message };
                    else if (section === 'notifications') this.notificationFeedback = { type: 'error', message };
                    else this.notify('error', message);
                    this.cdr.markForCheck();
                }
            });
    }

    // Proje genel alanları tek UpdateProject isteğiyle kaydedilir. Başarılı yanıtta ikinci GET atılmaz.
    private updateProject(changes: Partial<ProjectUpdateRequest>): void {
        if (!this.original || this.busy || this.settingsBusy !== null || this.isDeleted) return;
        const project = this.original;
        const request: ProjectUpdateRequest = {
            id: project.id,
            name: project.name || '',
            description: project.description || '',
            icon: project.icon || '',
            color: project.color || '',
            isActive: project.isActive,
            isFavorite: project.isFavorite,
            lang: project.lang || 'tr',
            coverImageUrl: project.coverImageUrl || null,
            allowedDomains: [...(project.allowedDomains || [])],
            ...changes
        };
        this.busy = 'save';
        this.form.disable({ emitEvent: false });
        this.projectSvc.UpdateProject(request)
            .pipe(take(1), defaultIfEmpty(null), takeUntil(this.destroyed$), finalize(() => {
                this.busy = null;
                this.form.enable({ emitEvent: false });
                this.cdr.markForCheck();
            }))
            .subscribe({
                next: result => {
                    if (!result || result.isError || result.data !== true) {
                        this.notify('error', result?.msg || 'Değişiklikler kaydedilemedi.');
                        return;
                    }
                    const updated = Object.assign(this.clone(project), this.clone(changes)) as ProjectDetailsModel;
                    this.original = updated;
                    this.project = this.clone(updated);
                    this.loadForm(updated);
                    this.saved.emit(this.clone(updated));
                    this.notify('success', 'Proje bilgileri başarıyla güncellendi.');
                },
                error: error => this.notify('error', this.errorText(error, 'Güncelleme sırasında bağlantı hatası oluştu.'))
            });
    }

    private updateFeedback(operation: UpdateOperation, message: string): void {
        if (operation === 'save-location') this.locationFeedback = { type: 'error', message };
        else this.notificationFeedback = { type: 'error', message };
        this.cdr.markForCheck();
    }

    private hydrateProjectSettings(project: ProjectDetailsModel | null): void {
        if (project && this.settingsProjectId === project.id) return;
        this.settingsProjectId = project?.id || '';
        this.location = new ProjectLocationModel();
        this.locationAvailable = false;
        this.locationDirty = false;
        this.locationLoaded = true;
        this.notificationsLoaded = true;
        this.ipEditing = this.emailEditing = this.phoneEditing = false;
        this.locationFeedback = this.notificationFeedback = null;
        this.ipControl.reset('');
        this.applyNotifications(null);
        if (project) {
            const value = this.readSettingsField(project, ['location']);
            const location = ProjectLocationModel.fromApi(value);
            if (location) { this.location = location; this.locationAvailable = true; this.ipControl.setValue(location.Ip); }
            this.applyNotifications(this.readSettingsField(project, ['notifications']));
        }
        this.syncLocationMap();
    }

    private syncLocationMap(): void { if (!this.mapRef || this.mapRef.hostView.destroyed) return; this.mapRef.setInput('latitude', this.locationAvailable ? this.location.Latitude : null); this.mapRef.setInput('longitude', this.locationAvailable ? this.location.Longitude : null); this.mapRef.setInput('disabled', this.settingsLocked); }
    private readSettingsField(value: unknown, names: string[]): unknown { if (!value || typeof value !== 'object') return undefined; const row = value as Record<string, unknown>; const key = Object.keys(row).find(item => names.some(name => item.toLowerCase() === name.toLowerCase())); return key === undefined ? undefined : row[key]; }
    private applyNotifications(value: unknown): void { const email = this.readSettingsField(value, ['email']); const phone = this.readSettingsField(value, ['phone']); this.savedNotifications = { Email: typeof email === 'string' ? email : '', Phone: typeof phone === 'string' ? phone : '' }; this.notificationEmailControl.reset(this.savedNotifications.Email); this.notificationPhoneControl.reset(this.savedNotifications.Phone); }
    private validIpAddress(value: string): boolean { if (!value || /[\s/%?#@\[\]]/.test(value)) return false; if (!value.includes(':')) return /^(?:0|[1-9]\d{0,2})(?:\.(?:0|[1-9]\d{0,2})){3}$/.test(value) && value.split('.').every(part => Number(part) <= 255); try { return new URL('http://[' + value + ']/').hostname.startsWith('['); } catch { return false; } }


    constructor(
        private readonly cdr: ChangeDetectorRef,
        private readonly route: ActivatedRoute,
        private readonly projectSvc: ProjectService,
        private readonly http: HttpClient
    ) {
        this.deleteNameControl.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.deleteError = '';
            });
    }

    ngOnInit(): void {
        this.projectId = this.project?.id || this.route.snapshot.paramMap.get('id');

        if (this.project) {
            return;
        }

        if (!this.projectId) {
            this.notify('error', 'Route içerisinde proje ID bulunamadı.');
            return;
        }

        this.getProject();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['project']) {
            this.applyProject(this.project);
        }
    }

    ngOnDestroy(): void {
        this.mapRef?.destroy();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    get view(): ProjectDetailsModel | null {
        return this.original;
    }

    get dirty(): boolean {
        return JSON.stringify(this.form.getRawValue()) !== JSON.stringify(this.initialValues);
    }

    get iconOptions(): string[] {
        const current = this.form.controls.icon.value;
        return current && !this.icons.includes(current) ? [current, ...this.icons] : this.icons;
    }

    get iconClass(): string {
        return this.form.controls.icon.value || 'bi bi-three-dots';
    }

    get iconColor(): string {
        return /^#[0-9a-fA-F]{6}$/.test(this.form.controls.color.value)
            ? this.form.controls.color.value
            : '#1f6feb';
    }

    get usagePercent(): number {
        const usage = this.original?.usageModel;
        return usage && usage.max > 0
            ? Math.min(100, Math.max(0, (usage.current / usage.max) * 100))
            : 0;
    }

    get languages(): string[] {
        const current = this.form.controls.lang.value;
        return [...new Set(['tr', 'en', current].filter(Boolean))];
    }

    get generalRows(): { label: string; value: string }[] {
        const p = this.original;
        if (!p) {
            return [];
        }
        return [
            { label: 'Proje ID', value: p.id },
            { label: 'Proje PID', value: String(p.pid) },
            { label: 'Kullanıcı ID', value: p.userId },
            { label: 'Proje anahtarı', value: p.projectKey },
            { label: 'Uygulama ID', value: p.applicationId },
            { label: 'Uygulama PID', value: String(p.applicationPid) },
            { label: 'Proje tipi', value: String(p.projectType) },
            { label: 'Hazırlık tipi', value: String(p.readyType) },
            { label: 'Kullanım tipi (enmUsageType)', value: String(p.enmUsageType) },
            { label: 'Oluşturulma tarihi', value: this.dateText(p.createdDate) },
            {
                label: 'Proje bitiş tarihi',
                value: this.dateText(p.projectEndDate, 'Belirtilmemiş'),
            },
            {
                label: 'Son kullanım tarihi',
                value: this.dateText(p.finalUsageDate, 'Henüz kullanılmadı'),
            },
            { label: 'Son veri IP', value: p.lastDataIP || '—' },
            {
                label: 'Son gelen veri tarihi',
                value: this.dateText(p.lastIncommingDataDate, 'Henüz veri alınmadı'),
            },
            { label: 'Yönlendirme bağlantısı', value: p.routeLink || '—' },
            { label: 'Hata durumu (isError)', value: p.isError ? 'Evet' : 'Hayır' },
            { label: 'Proje mesajı (msg)', value: p.msg || '—' },
        ];
    }

    get applicationRows(): { label: string; value: string }[] {
        const a = this.original?.applicationInfo;
        if (!a) {
            return [];
        }
        return [
            { label: 'Uygulama adı', value: a.name },
            { label: 'Açıklama', value: a.description },
            { label: 'Sağlayıcı', value: a.vendorName },
            { label: 'Sürüm', value: a.version },
            { label: 'PID', value: String(a.pid) },
            { label: 'Kategori', value: a.applicationCategoryText || '—' },
            { label: 'Uygulama türü', value: a.applicationTypeText || '—' },
            { label: 'Uygulama tür kodu', value: String(a.applicationType) },
            { label: 'Logo bağlantısı', value: a.logo || '—' },
        ];
    }

    setTab(tab: ProjectTab): void { this.currentTab = tab; }

    toggleFavorite(): void {
        if (!this.busy) {
            this.form.controls.isFavorite.setValue(!this.form.controls.isFavorite.value);
            this.form.controls.isFavorite.markAsDirty();
        }
    }

    reset(): void {
        if (!this.original || this.busy) {
            return;
        }
        this.loadForm(this.original);
        this.domainControl.reset('');
        this.domainError = '';
        this.feedback = null;
        this.showToken = false;
    }

    addDomain(): void {
        if (this.busy || this.settingsBusy !== null || this.isDeleted) {
            return;
        }

        const raw = this.domainControl.value.trim().toLowerCase();
        this.domainError = '';
        if (!raw) {
            this.domainError = 'Lütfen bir alan adı giriniz.';
            return;
        }
        let domain: string;
        try {
            const url = new URL(raw.includes('://') ? raw : 'https://' + raw);
            domain = url.hostname;
            if (
                !['http:', 'https:'].includes(url.protocol) ||
                url.username ||
                url.password ||
                url.port ||
                url.search ||
                url.hash ||
                (url.pathname !== '/' && url.pathname !== '') ||
                !domain ||
                domain.includes('*') ||
                !(
                    domain === 'localhost' ||
                    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(
                        domain,
                    )
                )
            ) {
                throw new Error();
            }
        } catch {
            this.domainError = 'Geçerli bir alan adı giriniz. Örnek: app.anameta.com';
            return;
        }
        const domains = this.form.controls.allowedDomains.value;
        if (domains.some((value) => value.toLowerCase() === domain)) {
            this.domainError = 'Bu alan adı zaten eklenmiş.';
            return;
        }
        this.form.controls.allowedDomains.setValue([...domains, domain]);
        this.form.controls.allowedDomains.markAsDirty();
        this.domainControl.reset('');
    }

    removeDomain(domain: string): void {
        if (this.busy) {
            return;
        }
        this.form.controls.allowedDomains.setValue(
            this.form.controls.allowedDomains.value.filter((value) => value !== domain),
        );
        this.form.controls.allowedDomains.markAsDirty();
    }

    save(): void {
        if (!this.original || this.busy || this.settingsBusy !== null || this.isDeleted || !this.dirty) return;
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            this.currentTab = 'general';
            this.notify('error', 'Lütfen proje adını ve renk değerini kontrol ediniz.');
            return;
        }
        const values = this.form.getRawValue();
        if (values.coverImageUrl.trim() && !this.validImageUrl(values.coverImageUrl.trim())) {
            this.currentTab = 'general';
            this.notify('error', 'Kapak görseli için geçerli bir HTTP/HTTPS bağlantısı veya / ile başlayan bir dosya yolu giriniz.');
            return;
        }
        this.feedback = null;
        this.updateProject({
            ...values,
            name: values.name.trim(),
            coverImageUrl: values.coverImageUrl.trim() || null,
            allowedDomains: [...values.allowedDomains]
        });
    }

    openDelete(event: Event): void {
        if (!this.original || this.busy || this.settingsBusy !== null || this.isDeleted) {
            return;
        }
        this.deleteTrigger = event.currentTarget as HTMLElement;
        this.deleteNameControl.reset('');
        this.deleteError = '';
        this.showToken = false;
        const dialog = this.deleteDialog.nativeElement;
        if (!dialog.open) {
            dialog.showModal();
            this.deleteInput.nativeElement.focus();
        }
    }

    closeDelete(): void {
        if (this.busy === 'delete') {
            return;
        }
        this.deleteDialog.nativeElement.close();
    }

    onDialogCancel(event: Event): void {
        if (this.busy === 'delete') {
            event.preventDefault();
        }
    }

    onDialogClosed(): void {
        this.deleteNameControl.reset('');
        this.deleteError = '';
        this.deleteTrigger?.focus();
    }

    trapDialogFocus(event: KeyboardEvent): void {
        if (event.key !== 'Tab') {
            return;
        }
        const elements = Array.from(
            this.deleteDialog.nativeElement.querySelectorAll<HTMLElement>(
                'button:not([disabled]), input:not([disabled])',
            ),
        );
        if (!elements.length) {
            event.preventDefault();
            return;
        }
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    confirmDelete(): void {
        if (!this.original || this.busy || this.settingsBusy !== null || this.isDeleted) {
            return;
        }

        if (this.deleteNameControl.value !== this.original.name) {
            this.deleteError = 'Silmek istediğiniz projenin adını giriniz.';
            this.deleteInput.nativeElement.focus();
            return;
        }

        const project = this.clone(this.original);

        this.busy = 'delete';
        this.feedback = null;
        this.deleteError = '';
        this.deleteNameControl.disable({ emitEvent: false });

        // DeleteProject tam ProjectDetailsModel bekler.
        this.projectSvc.DeleteProject(project)
            .pipe(
                take(1),
                defaultIfEmpty(null),
                switchMap((result) => {
                    if (!result || result.isError || result.data !== true) {
                        this.deleteError = result?.msg || 'Proje silinemedi.';
                        this.cdr.markForCheck();
                        return EMPTY;
                    }

                    this.deleteDialog.nativeElement.close();
                    this.applyProject(null);
                    this.isDeleted = true;
                    this.form.reset();
                    this.initialValues = this.clone(this.form.getRawValue());

                    // Silinen proje tekrar sorgulanır; ekranda eski bilgiler tutulmaz.
                    return this.projectSvc.GetProject(project.id).pipe(
                        take(1),
                        defaultIfEmpty(null),
                        catchError((error) => {
                            if (error?.status !== 404) {
                                this.notify('error', 'Proje silindi ancak silme sonrası kontrol tamamlanamadı.');
                            }

                            return of(null);
                        })
                    );
                }),
                takeUntil(this.destroyed$),
                finalize(() => {
                    this.busy = null;
                    this.deleteNameControl.enable({ emitEvent: false });
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (result) => {
                    this.deleted.emit(project.id);

                    if (this.feedback?.type === 'error') {
                        return;
                    }

                    if (result && !result.isError && result.data) {
                        this.notify('error', 'Silme başarılı bildirildi ancak proje sorgusunda hâlâ veri dönüyor.');
                        return;
                    }

                    this.notify('success', 'Proje başarıyla silindi.');
                },
                error: (error) => {
                    this.deleteError = this.errorText(error, 'Proje silinirken bağlantı hatası oluştu.');
                    this.cdr.markForCheck();
                }
            });
    }

    async copy(value: string | null | undefined): Promise<void> {
        if (!value) {
            this.notify('error', 'Kopyalanacak bir değer bulunamadı.');
            return;
        }
        try {
            await navigator.clipboard.writeText(value);
            this.notify('success', 'Değer panoya kopyalandı.');
        } catch {
            this.notify('error', 'Panoya kopyalanamadı. Tarayıcı izinlerini kontrol ediniz.');
        }
    }

    dateText(value: string | null | undefined, empty = '—'): string {
        if (!value || value.startsWith('0001-01-01')) {
            return empty;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime())
            ? empty
            : new Intl.DateTimeFormat('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }).format(
                  date,
              );
    }

    shortDate(value: string | null | undefined): string {
        if (!value || value.startsWith('0001-01-01')) {
            return '—';
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('tr-TR').format(date);
    }

    bytes(value: number): string {
        if (!Number.isFinite(value) || value <= 0) {
            return '0 B';
        }
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
        return (
            new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(
                value / 1024 ** index,
            ) +
            ' ' +
            units[index]
        );
    }

    number(value: number | null | undefined): string {
        return new Intl.NumberFormat('tr-TR').format(value || 0);
    }

    trackRow(_index: number, row: { label: string }): string {
        return row.label;
    }

    trackValue(_index: number, value: string): string {
        return value;
    }

    private notify(type: 'success' | 'error', message: string): void {
        this.feedback = { type, message };
        this.cdr.markForCheck();
        queueMicrotask(() =>
            this.feedbackPanel?.nativeElement.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth',
            }),
        );
    }

    private validImageUrl(value: string): boolean {
        if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) {
            return true;
        }
        try {
            const url = new URL(value);
            return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password;
        } catch {
            return false;
        }
    }

    private clone<T>(value: T): T {
        return JSON.parse(JSON.stringify(value)) as T;
    }

    private errorText(error: unknown, fallback: string): string {
        const response = error as { error?: { msg?: string; message?: string } };
        return response?.error?.msg || response?.error?.message || fallback;
    }

    private applyProject(project: ProjectDetailsModel | null): void {
        const previousSettingsTab = this.original?.id === project?.id && (this.currentTab === 'location' || this.currentTab === 'notifications') ? this.currentTab : null;
        this.project = project ? this.clone(project) : null;
        this.projectId = project?.id || this.projectId;
        this.feedback = null;
        this.currentTab = previousSettingsTab ?? 'general';
        this.isDeleted = false;
        this.showToken = false;
        this.logoFailed = false;
        this.original = project ? this.clone(project) : null;
        this.hydrateProjectSettings(project);

        if (!this.original) {
            return;
        }

        this.loadForm(this.original);

        if (this.original.isError) {
            this.notify('error', this.original.msg || 'Proje bilgileri yüklenirken hata oluştu.');
        }
    }

    private loadForm(project: ProjectDetailsModel): void {
        this.form.reset(
            {
                name: project.name || '',
                description: project.description || '',
                icon: project.icon || '',
                color: project.color || '',
                isActive: project.isActive,
                isFavorite: project.isFavorite,
                lang: project.lang || 'tr',
                coverImageUrl: project.coverImageUrl || '',
                allowedDomains: [...(project.allowedDomains || [])],
            },
            { emitEvent: false },
        );
        this.initialValues = this.clone(this.form.getRawValue());
    }
}
